<template>
  <div>
    <div class="row">
      <div 
        v-for="type in types" 
        :key="type.id" 
        class="col-xs-6 col-sm-3">
        <div class="box p-a">
          <transition 
            name="fade" 
            mode="out-in">
            <div 
              key="content" 
              class="content">
              <div class="pull-left m-r">
                <span :class="type.class">
                  <i 
                    class="material-icons" 
                    v-html="type.icon"/>
                </span>
              </div>
              <div class="clear">
                <span>
                  <h4 class="m-a-0 text-lg _300">
                    <span v-show="type.currency">{{ Currency }}</span> 
                    <span v-if="!loading">{{ type.currency ? new Intl.NumberFormat('en', {minimumFractionDigits: 2}).format(type.total) : formatNumber(type.total) }}</span>
                    <small 
                      v-show="loading" 
                      class="text-xs"><i class="fa fa-spinner fa-spin fa-2x"/></small>
                  </h4>
                </span>
                <small class="text-muted">{{ type.name }}</small>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12">
        <div class="box">
          <div class="box-header b-b">
            <h5>Sales Report</h5>
            <small>Shows cumulative sales by date</small>
          </div>
          <div class="box-tool">
            <ul class="nav">
              <li class="nav-item inline dropdown">
                <a 
                  data-toggle="dropdown" 
                  class="btn btn-default">Export</a>
                <div class="dropdown-menu dropdown-menu-scale pull-right">
                  <span class="dropdown-header">Export As</span>
                  <a 
                    class="dropdown-item" 
                    @click.prevent="exportAsFile('CSV')">CSV</a>
                  <a 
                    class="dropdown-item" 
                    @click.prevent="exportAsFile('PDF')">PDF</a>
                </div>
              </li>
            </ul>
          </div>
          <div class="box-body">
            <chart 
              ref="chart" 
              :options="lineChart" 
              :auto-resize="true"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
    props: {
        Currency: {
            type: String,
            default: 'RM',
        },
    },

    data() {
        return {
            loading: true,
            types: [
                {
                    id: 'total_sales',
                    name: 'Total Sales',
                    total: null,
                    class: 'w-48 rounded accent',
                    icon: '&#xe227;',
                    color: '#a88add',
                    currency: true,
                },
                {
                    id: 'total_taxes',
                    name: 'Taxable Sales',
                    total: null,
                    class: 'w-48 rounded primary',
                    icon: '&#xe5ca;',
                    color: '#0cc2aa',
                    currency: true,
                },
                {
                    id: 'total_orders',
                    name: 'Total Orders',
                    total: null,
                    class: 'w-48 rounded warn',
                    icon: '&#xe86e;',
                    color: '#fcc100',
                    currency: false,
                },
                {
                    id: 'total_pax',
                    name: 'Total Pax',
                    total: null,
                    class: 'w-48 rounded success',
                    icon: '&#xe8d3;',
                    color: '#6cc788',
                    currency: false,
                },
            ],
            lineChart: {
                tooltip: {
                    trigger: 'axis',
                },
                legend: {
                    data: [],
                },
                grid: {
                    x: 50,
                    x2: 50,
                },
                xAxis: {
                    data: [],
                    type: 'category',
                    animation: true,
                    boundaryGap: false,
                },
                yAxis: [
                    {
                        name: 'RM',
                        type: 'value',
                    },
                    {
                        name: 'Total',
                        type: 'value',
                    },
                ],
                series: [],
            },
        };
    },

    computed: {
        ...mapState('tillFilter', ['filters']),
        ...mapGetters(['dateFilter', 'drawerSessionId']),

        api() {
            return Laroute.route(
                'franchise.report.snapshot.getSnapshotSummary',
                { id: this.outlet },
            );
        },

        outlet() {
            return this.$store.getters.activeOutlet;
        },
    },

    watch: {
        outlet() {
            this.getSummary();
        },

        dateFilter(a, b) {
            if (a == b) return;
            this.getSummary();
        },

        drawerSessionId() {
            this.getSummary();
        },

        filters: function(to, from) {
            if (to === from) return;
            this.getSummary();
        },
    },

    created() {
        var that = this;
        this.types.forEach(function(value, index) {
            that.lineChart.series.push({
                name: value.name,
                type: 'line',
                data: [],
                smooth: true,
                animation: true,
                symbolSize: 10,
                yAxisIndex: index > 1 ? 1 : 0,
                itemStyle: {
                    normal: {
                        color: value.color,
                    },
                },
            });

            that.lineChart.legend.data.push({
                name: value.name,
                icon: 'circle',
            });
        });
    },

    mounted() {
        this.getSummary();
        var that = this;
        window.onresize = function() {
            that.$refs.chart.resize();
        };
    },

    methods: {
        getSummary() {
            var that = this;
            this.loading = true;
            var chart = this.$refs.chart;
            chart.showLoading({
                text: 'loading...',
            });

            let options;

            options = {
                since: this.$store.getters.startDate.format(),
                until: this.$store.getters.endDate.format(),
                till_id: this.$store.getters.drawerSessionId,
                filters: this.filters,
            };

            this.$http.get(this.api, { params: options }).then(response => {
                var data = response.data;
                this.types = this.types.map(obj => {
                    obj.total = data[obj.id];
                    return obj;
                });

                if (data.graph_data.hourly) {
                    that.lineChart.xAxis.data = data.graph_data.total_sales.map(
                        obj => moment(obj.date).format('hh:mm A'),
                    );
                } else {
                    that.lineChart.xAxis.data = data.graph_data.total_sales.map(
                        obj =>
                            moment(obj.date)
                                .format('ddd - DD MMM YYYY')
                                .toUpperCase(),
                    );
                }

                that.lineChart.series[0].data = data.graph_data.total_sales.map(
                    obj => obj.total,
                );
                that.lineChart.series[1].data = data.graph_data.taxable_taxes.map(
                    obj => obj.total,
                );
                that.lineChart.series[2].data = data.graph_data.total_orders.map(
                    obj => obj.total,
                );
                that.lineChart.series[3].data = data.graph_data.total_pax.map(
                    obj => obj.total,
                );

                this.loading = false;
                chart.hideLoading();
            });
        },

        exportAsFile(type) {
            let url = Laroute.route(
                'franchise.report.snapshot.exportGraphData',
                { id: this.outlet, type: type },
            );
            let options = {
                since: this.$store.getters.startDate.format(),
                until: this.$store.getters.endDate.format(),
                filters: this.filters,
            };
            window.location.href = url + '?' + $.param(options);
        },

        formatNumber(number) {
            return numeral(number).format('0,0');
        },
    },
};
</script>

<style lang="scss" scoped>
.echarts {
    width: 100%;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/snapshot/vue_snapshot.vue